import { log2 } from "./utils/log2";
import { sendSocketMessage, socketMessageStream } from "./utils/socketStream";
import store from "./store";
import router from "./router";
import _ from "lodash";
import { incomingChatSound, newConversationSound, newMessageSound } from "./utils/notificationSounds";
import { systemMessageStream, SystemMessageTypes, publishMessage } from "./utils/systemPubSub";
import { filter, map, timeout, first } from "rxjs/operators";
import feature from "./utils/SiteFeatures";
import { message } from "ant-design-vue";
import { i18n } from "./language";

let log = log2("ChatWatcherV2");
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let singletonInstance = null;

const COMMANDS = {
    SET_WHATSAPP_CONTACT_STATUS: "user whatsapp-contact-status",
    WATCH_CONTACTS: "user watch-contacts",
    USER_START_CHAT: "user start-chat",
    TRANSLATE_MESSAGE: "user translate-message",
};

const RESPONSE_COMMANDS = {
    MULTI_CONVERSATION_ADD: "multi_conversation_add",
    CONVERSATION_CLOSED: "conversation_destroyed",
    NEW_CHAT_STARTED: "new_chat_started",
    CONVERSATION_ASSIGNED: "conversation_add",
    REFRESH_CONTACT: "refresh_contact",
    MESSAGE: "message",
    MESSAGE_UPDATED_PARTIAL: "message_updated_partial",
    MESSAGE_DELETED: "message_deleted",
    TRANSLATE_MESSAGE: "translate_message",
    CHAT_AGENTS_CHANGED: "chat_agents_changed",
    AGENT_JOINED_CHAT: "agent_added_to_chat",
    AGENT_LEAVE_CHAT: "agent_leave_from_chat",
    VISITOR_UPDATED: "visitor_updated",
    WHATSAPP_CONTACT_STATUS: "whatsapp_contact_status",
    CONTACT_VISIT_PAGE_CHANGED: "visitor_url_changed",
    JOIN_CHAT_IMMEDIATELY: "join_chat_immediately",
    OWNER_UPDATED: "owner_updated",
    NOTE_ADDED: "note_added",
    NOTE_DELETED: "note_deleted",
    VISITOR_BULK_OPERATIONS: "visitor_bulk_operations",
};

const TABLE_CONTACT_ACTIONS = {
    ADD_CONTACT: "add_contact",
    UPDATE_CONTACT: "update_contact",
    REMOVE_CONTACT: "remove_contact",
    UPDATE_CONTACT_FIELDS: "update_contact_fields",
};

const CONVERSATION_STATE_MAP = {
    all: ["active", "lost", "new", "chatbot", "destroyed"],
    open: ["active", "lost", "new", "chatbot"],
    closed: ["destroyed"],
};

const playSound = _.debounce((type) => {
    const userInteracted = store.state.app.userInteracted;
    if (!userInteracted) return;
    const sounds = {
        [RESPONSE_COMMANDS.MESSAGE]: newMessageSound(),
        [RESPONSE_COMMANDS.NEW_CHAT_STARTED]: newConversationSound(),
        [RESPONSE_COMMANDS.CONVERSATION_ASSIGNED]: incomingChatSound(),
    };
    const sound = sounds[type];
    if (sound) sound.play();
}, 1000);
let changeCountForChannelAndSettings = 0;
export default class ChatWatcherV2 {
    constructor() {
        this.playedSounds = {};
        this.listeners = [];
        this.inited = false;
        this.flatRequests = new Map();
        this.tabVisible = true;

        this.unitTestEnabled = false;
        this.unitTestResultGroups = {};

        // Stats polling
        this.needsStatsUpdate = false;
        this.statsPollingInterval = null;
        this.statsCheckInterval = null;

        this.init();
    }

    static create() {
        if (!singletonInstance) singletonInstance = new ChatWatcherV2();
        return singletonInstance;
    }

    incUnitTestFor(contactName, action = "Contact Connected") {
        let groupName = (contactName || "").match(/\[(.*?)\]/)?.[1] || "";
        if (!groupName) groupName = "Unknown";
        if (!this.unitTestEnabled) return;
        if (!this.unitTestResultGroups[groupName]) {
            this.unitTestResultGroups[groupName] = {
                "Contact Connected": 0,
                "Message Received": 0,
            };
        }
        this.unitTestResultGroups[groupName][action]++;
    }

    playSound(conversationId, type) {
        const played = _.get(this.playedSounds[type], conversationId, false);
        if (played) return;
        playSound(type);
        this.playedSounds[type] = {
            ...(this.playedSounds[type] || {}),
            [conversationId]: true,
        };
    }

    async init() {
        this.inited = true;
        await this.fetchInitialData();
        this.initWatchers();
        this.initListeners();
        this.initStatsPolling();
        this.getContacts(true);
        log.debug("ChatWatcherV2 inited.");
        // setInterval(() => {
        //     if (this.unitTestEnabled) {
        //         console.group("Unit Test Results");
        //         for (const groupName in Object.keys(this.unitTestResultGroups)) {
        //             console.group(groupName);
        //             const groupResult = this.unitTestResultGroups[groupName];
        //             console.table(
        //                 Object.keys(groupResult).map((action) => ({
        //                     Action: action,
        //                     Count: groupResult[action],
        //                 }))
        //             );
        //             console.groupEnd();
        //         }
        //         console.groupEnd();
        //     }
        // }, 10000);
    }

    resetState() {
        store.dispatch("contact/resetState");
    }

    initStatsPolling() {
        // 60 saniyede bir otomatik stats çek
        this.statsPollingInterval = setInterval(() => {
            this.fetchFilterStats().catch((err) => {
                log.error("Stats polling error", err);
            });
        }, 60000);

        // 10 saniyede bir bayrak kontrolü yap
        this.statsCheckInterval = setInterval(() => {
            if (this.needsStatsUpdate) {
                this.needsStatsUpdate = false;
                this.fetchFilterStats().catch((err) => {
                    log.error("Stats check error", err);
                });
            }
        }, 5000);
    }

    async fetchFilterStats() {
        try {
            const result = await store.dispatch("contact/getFilterStats", {
                source: "ChatWatcherV2.fetchFilterStats",
            });

            if (result && result.counts) {
                log.info("fetchFilterStats -> counts received", result.counts);

                store.dispatch("contact/setCounts", {
                    all: result.counts.all || 0,
                    open: result.counts.open || 0,
                    closed: result.counts.closed || 0,
                });
            }
        } catch (err) {
            log.error("fetchFilterStats error", err);
        }
    }

    requestStatsUpdate() {
        this.needsStatsUpdate = true;
    }

    restartStatsPolling() {
        // Mevcut interval'ları temizle
        if (this.statsPollingInterval) {
            clearInterval(this.statsPollingInterval);
            this.statsPollingInterval = null;
        }
        if (this.statsCheckInterval) {
            clearInterval(this.statsCheckInterval);
            this.statsCheckInterval = null;
        }

        // Bayrağı sıfırla
        this.needsStatsUpdate = false;

        // Yeni interval'ları başlat
        this.initStatsPolling();

        // Hemen bir stats fetch yap
        this.fetchFilterStats().catch((err) => {
            log.error("Initial stats fetch after restart error", err);
        });
    }

    async getContacts(firstInit = false) {
        await store.dispatch("contact/getFilteredContacts", {
            source: "ChatWatcherV2.getContacts",
            count: true,
        });
        const selectedContact = router.currentRoute.query.contact;
        if (firstInit && selectedContact) {
            const shared = store.state.app.route.query.shared;
            const isShowingContactList = await ChatWatcherV2.isShowingContactList(
                selectedContact,
                true,
                null,
                null,
                "getContacts"
            );
            const contact = await this.getContact(selectedContact);
            if (isShowingContactList) {
                if (contact) {
                    store.dispatch("contact/setSelectedContact", contact);
                    return;
                }
            } else if (shared === "true") {
                const isShowingContact = await ChatWatcherV2.isShowingContactList(
                    selectedContact,
                    false,
                    null,
                    null,
                    "getContacts"
                );
                if (isShowingContact) {
                    store.dispatch("app/addNewWindow", {
                        type: "grid-chat-window-contact",
                        title: contact.fullname || contact.email || contact.phone || "Bilinmeyen Kişi",
                        autoClose: false,
                        multiple: false,
                        fullShow: true,
                        footer: false,
                        payload: {
                            contactId: contact._id,
                        },
                    });
                    return;
                }
            }
            router.replace({ query: { ...store.state.app.route.query, contact: undefined, shared: undefined } });
        }
    }

    async fetchInitialData() {
        await store.dispatch("contact/getContactListFilters").catch(console.error);
        const inbox = store.state.app.route.query.inbox;
        const filter = store.state.contact.filters.find((filter) => filter.key === inbox);
        if (filter) {
            store.dispatch("contact/setSelectedFilterId", filter.id);
        } else if (store.state.contact.filters.length > 0 && !inbox) {
            const isChatListPage = router.currentRoute.name === "chat";
            const isInboxTab = store.state.app.route.query.tab === "inbox";
            if (isChatListPage && isInboxTab) {
                router.replace({ query: { ...store.state.app.route.query, inbox: store.state.contact.filters[0].key } });
            }
        }

        const state = store.state.app.route.query.state;
        if (state) {
            store.dispatch("contact/setSelectedState", state);
        }
    }

    initWatchers() {
        store.watch(
            (state) => state.contact.selectedContact,
            async (selectedContact, oldContact) => {
                if (selectedContact) {
                    router.replace({ query: { ...store.state.app.route.query, contact: selectedContact._id } });
                }

                // Sticky Logic Cleanup:
                // Eğer önceki seçili kişi artık filtreye uymuyorsa listeden kaldır.
                // Önceden seçili olduğu için silinmemişti.
                if (oldContact && (!selectedContact || selectedContact._id !== oldContact._id)) {
                    const contactId = oldContact._id;
                    // Listede hala var mı?
                    const inList = store.state.contact.contacts.some((c) => c._id === contactId);
                    if (inList) {
                        const isShowing = await ChatWatcherV2.isShowingContactList(
                            contactId,
                            true,
                            null,
                            null,
                            "selectedContactChanged"
                        );
                        if (!isShowing) {
                            log.info("Sticky cleanup: Removing old contact that no longer matches filter", contactId);
                            await store.dispatch("contact/removeContact", contactId);
                            this.requestStatsUpdate();
                            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                                data: { contactId: contactId },
                            });
                        }
                    }
                }
            },
            { deep: true }
        );

        // Ayarlar veya Kanallar değişirse listeyi yenile (Debounced)
        store.watch(
            (state) => [
                state.setting.settings.onlyAdminsShowsNoOwnerContacts,
                state.setting.settings.subject,
                state.channel.channels,
            ],
            _.debounce(() => {
                changeCountForChannelAndSettings++;
                if (changeCountForChannelAndSettings > 2) {
                    const isChatPage = router.currentRoute?.name === "chat";
                    if (isChatPage) {
                        log.info("Critical settings or channels changed, refreshing contacts list...", { isChatPage });
                        this.getContacts();
                    }
                }
            }, 2000),
            { deep: true }
        );

        store.watch(
            (state) => state.contact.selectedState,
            (selectedState) => {
                if (selectedState) {
                    router.replace({ query: { ...store.state.app.route.query, state: selectedState } });
                }
            },
            { deep: true }
        );

        store.watch(
            (state) => state.socketStatus.socket_connected,
            (socketConnected) => {
                const connectionCount = store.state.socketStatus?.connection_count || 0;
                if (socketConnected && connectionCount > 0) this.reSync();
            }
        );

        store.watch(
            (state) => state.app.route.query.inbox,
            (view) => {
                log.info("watch - view", view);
                const filters = store.state.contact.filters;
                const filter = filters.find((filter) => filter.key === view);
                const currentView = store.state.app.route.query.view;
                const currentTab = store.state.app.route.query.tab;
                if (currentTab !== "inbox") {
                    log.warn("watch - invalid tab", currentTab);
                    return;
                }
                if (filter) {
                    store.dispatch("contact/setSelectedFilterId", filter.id);
                } else if (filters.length > 0 && currentView !== "tab" && !view) {
                    router.replace({ query: { ...store.state.app.route.query, inbox: filters[0].key } });
                }
            },
            { immediate: true, deep: true }
        );

        store.watch(
            (state) => state.contact.filters,
            (filters, oldFilters) => {
                const currentTab = store.state.app.route.query.tab;
                if (currentTab !== "inbox") {
                    log.warn("watch - invalid tab", currentTab);
                    return;
                }

                const inbox = store.state.app.route.query.inbox;
                const filter = filters.find((filter) => filter.key === inbox);
                if (filter) {
                    if (filter.id === store.state.contact.selectedFilterId) return;
                    store.dispatch("contact/setSelectedFilterId", filter.id);
                } else if (filters.length > 0 && !inbox) {
                    router.replace({ query: { ...store.state.app.route.query, inbox: filters[0].key } });
                }
            },
            { deep: true, immediate: true }
        );

        store.watch(
            (state) => [
                state.contact.selectedState,
                state.contact.selectedSortField,
                state.contact.selectedSortDir,
                state.contact.selectedFilterId,
                state.contact.activeView,
            ],
            (newFilterState, oldFilterState) => {
                const activeView = newFilterState[4];
                if (activeView !== "chat") {
                    return;
                }
                if (!oldFilterState) {
                    return;
                }
                const isChanged = newFilterState
                    .slice(0, 4)
                    .some((newState, index) => newState !== oldFilterState[index]);
                if (isChanged) {
                    store.dispatch("contact/clearCachedContacts");
                    store.dispatch("contact/getFilteredContacts", {
                        source: "ChatWatcherV2.stateChanged",
                        count: true,
                    });
                }
            },
            { deep: true }
        );

        store.watch(
            (state) => state.contact.selectedFilterId,
            (selectedFilterId, oldSelectedFilterId) => {
                if (selectedFilterId === oldSelectedFilterId) return;
                const filters = store.state.contact.filters;
                const filter = filters.find((filter) => filter.id === selectedFilterId);
                if (filter) {
                    store.dispatch(
                        "contact/updateContactListFilters",
                        filters.map((f) => {
                            if (f.id === selectedFilterId) {
                                return {
                                    ...f,
                                    viewed: true,
                                };
                            }
                            return f;
                        })
                    );
                }

                // Filtre değiştiğinde polling'i yeniden başlat ve cachedContacts'ı temizle
                if (oldSelectedFilterId && selectedFilterId !== oldSelectedFilterId) {
                    log.info("Filter changed, restarting stats polling", {
                        oldSelectedFilterId,
                        selectedFilterId,
                    });
                    store.dispatch("contact/clearCachedContacts");
                    this.restartStatsPolling();
                }
            },
            { deep: true, immediate: true }
        );
    }

    initListeners() {
        this.listeners.forEach((cancel) => cancel());
        this.listeners = [];
        const socketStream = socketMessageStream.subscribe(({ cmd, data }) => {
            if (cmd === RESPONSE_COMMANDS.MULTI_CONVERSATION_ADD) {
                log.debug("socket.MULTI_CONVERSATION_ADD", data);
                this.multiConversationAdd(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.CONVERSATION_CLOSED) {
                log.debug("socket.CONVERSATION_CLOSED", data);
                this.conversationClosed(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.REFRESH_CONTACT) {
                log.debug("socket.REFRESH_CONTACT", data);
                this.refreshContact(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.NEW_CHAT_STARTED) {
                log.debug("socket.NEW_CHAT_STARTED", data);
                this.newContactConnected(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.CONVERSATION_ASSIGNED) {
                log.debug("socket.CONVERSATION_ASSIGNED", data);
                this.newContactConnected(data, true).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.MESSAGE) {
                if (!data.isTeamChat) this.newMessageReceived(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.MESSAGE_DELETED) {
                this.messageDeleted(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.MESSAGE_UPDATED_PARTIAL) {
                this.messageUpdatedPartial(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.TRANSLATE_MESSAGE) {
                this.translateMessage(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_JOINED_CHAT) {
                log.debug("socket.AGENT_JOINED_CHAT", data);
                this.agentJoinedChat(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.AGENT_LEAVE_CHAT) {
                log.debug("socket.AGENT_LEAVE_CHAT", data);
                this.agentLeaveChat(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.VISITOR_UPDATED) {
                log.debug("socket.VISITOR_UPDATED", data);
                this.visitorUpdated(data);
            } else if (cmd === RESPONSE_COMMANDS.WHATSAPP_CONTACT_STATUS) {
                this.whatsappContactStatus(data);
            } else if (cmd === RESPONSE_COMMANDS.CONTACT_VISIT_PAGE_CHANGED) {
                this.contactVisitPageChanged(data);
            } else if (cmd === RESPONSE_COMMANDS.OWNER_UPDATED) {
                this.ownerChanged(data);
            } else if (cmd === RESPONSE_COMMANDS.NOTE_ADDED) {
                this.noteAdded(data);
            } else if (cmd === RESPONSE_COMMANDS.NOTE_DELETED) {
                this.noteDeleted(data);
            } else if (cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY) {
                this.joinChatImmediately(data);
            } else if (cmd === RESPONSE_COMMANDS.VISITOR_BULK_OPERATIONS) {
                log.debug("socket.VISITOR_BULK_OPERATIONS", data);
                this.visitorBulkOperations(data).then(_.noop).catch(console.error);
            } else if (cmd === RESPONSE_COMMANDS.CHAT_AGENTS_CHANGED) {
                log.debug("socket.CHAT_AGENTS_CHANGED", data);
                this.chatAgentsChanged(data).then(_.noop).catch(console.error);
            } else if (cmd === "user_scope_changed") {
                log.info("socket.user_scope_changed", data);
                store.dispatch("contact/getContactListFilters");
                this.getContacts();
            } else if (cmd === "visitor_bulk_operation_completed") {
                log.info("visitor_bulk_operation_completed", data);
                // İşlemi yapan ben değilsem mesaj göster
                const myUserId = store.state.auth.user._id;
                if (router.currentRoute.name === "chat") {
                    if (data.userId !== myUserId) {
                        message.info(
                            `${
                                data.username || "Bir kullanıcı"
                            } kişiler üzerinde toplu güncelleme yaptı, liste yenileniyor.`
                        );
                    }
                    store.dispatch("contact/getContactListFilters");
                    this.getContacts();
                }
            }
        });

        const tabVisibilitySub = systemMessageStream
            .pipe(
                filter((x) => x.type === SystemMessageTypes.TAB_VISIBILITY_CHANGE),
                map((x) => x.payload)
            )
            .subscribe((payload) => {
                const { visible } = payload;
                this.tabVisibleAction(visible);
            });

        this.listeners.push(() => {
            socketStream.unsubscribe();
            tabVisibilitySub.unsubscribe();
        });
    }

    tabVisibleAction(visible) {
        if (visible) {
            this.totalUnreadCount = 0;
        } else {
            const site = store.state.site.site;
            document.title =
                this.totalUnreadCount > 0
                    ? `(${this.totalUnreadCount} new message) Supsis - ${
                          site.alias.charAt(0).toUpperCase() + site.alias.slice(1)
                      }`
                    : `Supsis  - ${site.alias.charAt(0).toUpperCase() + site.alias.slice(1)}`;
        }
        this.tabVisible = visible;
    }

    async getContact(contactId, options = {}) {
        const { ignoreSource, cachePrefix, disableCache } = options;
        const key = cachePrefix ? `${cachePrefix}.${contactId}` : contactId;
        log.info("getContact debug", {
            key,
            disableCache,
            flatRequests: this.flatRequests,
            ignoreSource,
            request: this.flatRequests.get(key),
        });
        if (this.flatRequests.has(key) && !disableCache) {
            return this.flatRequests.get(key);
        }
        const request = store.dispatch("contact/getContact", { contactId, ignoreSource, cache: !disableCache });
        this.flatRequests.set(key, request);
        const result = await request.catch(() => null);
        this.flatRequests.delete(key);
        return result;
    }

    static checkFilterCondition(contactValue, operator, filterValue) {
        if (contactValue === null || contactValue === undefined) {
            return true;
        }

        if (operator === "equals") {
            return contactValue === filterValue;
        } else if (operator === "contains") {
            return contactValue.toLowerCase().includes(filterValue.toLowerCase());
        } else if (operator === "less_than") {
            return contactValue < filterValue;
        } else if (operator === "less_than_or_equal") {
            return contactValue <= filterValue;
        } else if (operator === "greater_than") {
            return contactValue > filterValue;
        } else if (operator === "greater_than_or_equal") {
            return contactValue >= filterValue;
        } else if (operator === "between") {
            if (!Array.isArray(filterValue) || filterValue.length !== 2) {
                return true;
            }
            return contactValue >= filterValue[0] && contactValue <= filterValue[1];
        }

        return true;
    }

    static getOwnedChannels(user) {
        const channels = store.state.channel.channels;
        const authUser = user || store.state.auth.user;
        return channels.filter((channel) => (channel?.visibility?.owners || []).includes(authUser._id));
    }

    static listAvailableChannels(user) {
        const channels = store.state.channel.channels;
        const authUser = user || store.state.auth.user;
        const adminUser = user || store.state.auth.adminUser;

        const isSuperAdmin =
            !!adminUser ||
            _.get(authUser, "isSuperAdmin", false) ||
            _.get(authUser, "role.isRoot", false) ||
            _.get(authUser, "isOwner", false);
        if (isSuperAdmin) return channels;

        const userDepartmentIds = Array.isArray(authUser.subjects) ? authUser.subjects : [];
        return channels.filter((channel) => {
            const visibility = channel.visibility || { users: [], departments: [], owners: [] };
            visibility.users = Array.isArray(visibility.users) ? visibility.users : [];
            visibility.departments = Array.isArray(visibility.departments) ? visibility.departments : [];
            visibility.owners = Array.isArray(visibility.owners) ? visibility.owners : [];

            const isPublicChannel = visibility.users.length === 0 && visibility.departments.length === 0;
            if (isPublicChannel) return true;

            const isVisibleToUser = visibility.users.includes(authUser._id);
            const isVisibleToDepartment = visibility.departments.some(
                (departmentId) => userDepartmentIds.includes(departmentId) || userDepartmentIds.length === 0
            );

            const isVisibleToOwner = visibility.owners.includes(authUser._id);
            return isVisibleToUser || isVisibleToDepartment || isVisibleToOwner;
        });
    }

    async joinConversation(conversationId, contactId, { reconnect = false, autoAssign = false, selectedContact } = {}) {
        const userId = store.state.auth.user._id;
        const siteId = store.state.site.site._id;
        sendSocketMessage("user join-conversation", {
            userId,
            conversationId,
            siteId,
            reconnect,
            autoAssign,
        });
        if (selectedContact) {
            const contact = await this.getContact(contactId, { ignoreSource: "joinConversation" }).catch(() => {
                log.debug("joinConversation -> contact not found", contactId);
            });
            if (contact) store.dispatch("chat/setSelectedContact", contact);
        }
    }

    static async isShowingContactList(
        contactIdOrData,
        filterControl = true,
        searchField,
        searchQuery,
        ignoreSource = "unknown",
        filterId = null
    ) {
        log.info("isShowingContactList -> contactId", contactIdOrData);
        const watcher = ChatWatcherV2.create();
        const isFullContact = typeof contactIdOrData === "object" && Object.keys(contactIdOrData).length >= 1;
        let contact = null;
        if (isFullContact) contact = contactIdOrData;
        else {
            contact = await watcher
                .getContact(contactIdOrData, { ignoreSource: `${ignoreSource}.isShowingContactList` })
                .catch(() => {
                    log.debug("isShowingContactList -> contact not found", contactIdOrData);
                });
            log.info("isShowingContactList -> contact", contact);
        }
        const contactId = _.get(contact, "_id");
        if (!contact) {
            log.info("isShowingContactList -> contact not found", contactId);
            return false;
        }

        log.info("isShowingContactList -> contact", contact);
        const filters = store.state.contact.filters;
        let selectedFilter = store.getters["contact/selectedFilter"];
        if (filterId) {
            const filter = filters.find((f) => f.id === filterId);
            log.info("isShowingContactList -> filter", { filter, filterId });
            if (filter) selectedFilter = filter;
        }
        const selectedState = store.state.contact.selectedState;

        const authUser = store.state.auth.user;
        const settings = store.state.setting.settings;
        const channels = store.state.channel.channels;
        const departments = store.state.subject.subjects.data;

        const webchatChannelId = channels.find((channel) => channel.type === "webchat")?._id;
        const ownedChannels = channels.filter((channel) => (channel?.visibility?.owners || []).includes(authUser._id));

        const isUserRoot = _.get(authUser, "role.isRoot", false);
        const isSiteOwner = _.get(authUser, "isOwner", false);
        const isSuperAdmin = _.get(authUser, "isSuperAdmin", false) || !!store.state.auth.adminUser;
        const onlyAdminsShowsNoOwnerContacts = !!_.get(settings, "onlyAdminsShowsNoOwnerContacts", false);

        const isPrivilegedUser = isUserRoot || isSiteOwner || isSuperAdmin;

        if (!isPrivilegedUser) {
            if (contact.channel) {
                // 1. KANALI OLANLAR
                const channel = channels.find((c) => c._id === contact.channel);
                if (!channel) {
                    log.info("isShowingContactList -> reason", { reason: "privacy.channelNotFound" });
                    return false;
                }

                const visibility = channel.visibility || { users: [], departments: [], owners: [] };
                const userList = visibility.users || [];
                const deptList = visibility.departments || [];
                const ownerList = visibility.owners || [];

                const isChannelPublic = userList.length === 0 && deptList.length === 0;
                const isInUserList = userList.includes(authUser._id);
                let userDeptIds = authUser.subjects || [];
                userDeptIds = _.uniq(userDeptIds);
                if (userDeptIds.length === 0) {
                    userDeptIds = departments.map((d) => d._id.toString());
                }
                const isInDeptList = deptList.some((d) => userDeptIds.includes(d));
                const isChannelOwner = ownerList.includes(authUser._id);

                const hasChannelAccess = isChannelPublic || isInUserList || isInDeptList || isChannelOwner;

                if (!hasChannelAccess) {
                    log.info("isShowingContactList -> reason", { reason: "privacy.noChannelAccess" });
                    return false;
                }

                // Kanal İçi Görünürlük
                let isVisible = false;

                // 1. Kanal Sahibi ise
                if (isChannelOwner) isVisible = true;
                // 2. Contact Sahibi ise
                else if (contact.owner === authUser._id) isVisible = true;
                // 3. Sahipsiz ve Ayar İzin Veriyorsa
                else if (!contact.owner && !onlyAdminsShowsNoOwnerContacts) isVisible = true;

                if (!isVisible) {
                    log.info("isShowingContactList -> reason", { reason: "privacy.channelRestrictions" });
                    return false;
                }
            } else {
                // 2. KANALI OLMAYANLAR
                // Sadece Owner görebilir
                if (contact.owner !== authUser._id) {
                    log.info("isShowingContactList -> reason", { reason: "privacy.noChannelAndNotOwner" });
                    return false;
                }
            }
        }
        // === PRIVACY END ===

        //#region Subject/Department Privacy Filter
        const isSubjectEnabled = !!_.get(settings, "subject", false);
        const userSubjects = authUser.subjects || [];

        // Only apply if subject setting is enabled, user is not privileged, and user has restricted subjects
        if (isSubjectEnabled && !isPrivilegedUser && userSubjects.length > 0) {
            const isOwner = contact.owner === authUser._id;
            const isChannelOwner = ownedChannels.some((c) => c._id === contact.channel);

            if (!isOwner && !isChannelOwner) {
                const contactDepartments = contact.relatedDepartments || [];
                // Normalize contact departments to IDs (handle populated objects or raw IDs)
                const contactDepartmentIds = contactDepartments.map((d) => (d && d._id ? d._id : d));

                // Check if contact has at least one of the user's allowed subjects
                const hasAccess = contactDepartmentIds.some((deptId) => userSubjects.includes(deptId));

                if (!hasAccess) {
                    log.info("isShowingContactList -> reason", { reason: "privacy.subjectRestriction" });
                    return false;
                }
            }
        }
        //#endregion

        const filtersMap = (filterControl ? selectedFilter.filters : [])
            .filter((filter) => filter.value !== null && filter.value !== undefined && filter.value !== "")
            .reduce((acc, filter) => {
                acc[filter.field] = filter;
                return acc;
            }, {});

        log.info("isShowingContactList -> filtersMap", filtersMap);

        //#region Last Trigger State Filter
        const lastTriggerState = _.get(filtersMap, "lastTriggerState.value", null);
        if (lastTriggerState && lastTriggerState.selectValue && lastTriggerState.inputValue) {
            const triggerType = _.get(contact, "lastTriggerState.type", null);
            const triggerName = _.get(contact, "lastTriggerState.name", "");
            if (
                triggerType !== lastTriggerState.selectValue ||
                !triggerName.toLowerCase().includes(lastTriggerState.inputValue.toLowerCase())
            ) {
                log.info("isShowingContactList -> reason", { reason: "lastTriggerState" });
                return false;
            }
        }
        //#endregion

        //#region Last Conversation Chatbot Filter
        const lastConversationChatbot = _.get(filtersMap, "lastConversationChatbot.value", null);
        if (lastConversationChatbot !== null && lastConversationChatbot !== undefined) {
            const chatbotValue = lastConversationChatbot === "true" || lastConversationChatbot === true;
            if (contact.lastConversationChatbot !== chatbotValue) {
                log.info("isShowingContactList -> reason", { reason: "lastConversationChatbot" });
                return false;
            }
        }
        //#endregion

        //#region Unread Filter
        const unreadFilter = _.get(filtersMap, "unread.value", null);
        if (unreadFilter !== null && unreadFilter !== undefined) {
            const unreadValue = unreadFilter === "true" || unreadFilter === true;
            if (!!contact.unread !== unreadValue) {
                log.info("isShowingContactList -> reason", { reason: "unread" });
                return false;
            }
        }
        //#endregion

        //#region Answer Status Filter
        const answerStatusFilter = _.get(filtersMap, "answerStatus.value", null);
        if (answerStatusFilter) {
            if (contact.answerStatus !== answerStatusFilter) {
                log.info("isShowingContactList -> reason", { reason: "answerStatus" });
                return false;
            }
        }
        //#endregion

        //#region Last Conversation Is Missed Filter
        const lastConversationIsMissed = _.get(filtersMap, "lastConversationIsMissed.value", null);
        if (lastConversationIsMissed !== null && lastConversationIsMissed !== undefined) {
            const isMissedValue = lastConversationIsMissed === "true" || lastConversationIsMissed === true;
            if (contact.lastConversationIsMissed !== isMissedValue) {
                log.info("isShowingContactList -> reason", { reason: "lastConversationIsMissed" });
                return false;
            }
        }
        //#endregion

        //#region Last Broadcast Info Filter
        const lastBroadcastInfo = _.get(filtersMap, "lastBroadcastInfo.value", null);
        if (lastBroadcastInfo) {
            if (lastBroadcastInfo.broadcast) {
                const broadcastId = _.get(contact, "lastBroadcastInfo.broadcast", null);
                if (broadcastId !== lastBroadcastInfo.broadcast) {
                    log.info("isShowingContactList -> reason", { reason: "lastBroadcastInfo.broadcast" });
                    return false;
                }
            }

            if (lastBroadcastInfo.status) {
                const broadcastStatus = _.get(contact, "lastBroadcastInfo.status", null);
                if (broadcastStatus !== lastBroadcastInfo.status) {
                    log.info("isShowingContactList -> reason", { reason: "lastBroadcastInfo.status" });
                    return false;
                }
            }

            if (lastBroadcastInfo.answered !== null && lastBroadcastInfo.answered !== undefined) {
                const answeredValue = lastBroadcastInfo.answered === "true" || lastBroadcastInfo.answered === true;
                const broadcastAnswered = _.get(contact, "lastBroadcastInfo.answered", false);
                if (broadcastAnswered !== answeredValue) {
                    log.info("isShowingContactList -> reason", { reason: "lastBroadcastInfo.answered" });
                    return false;
                }
            }
        }
        //#endregion

        //#region Zoho Info Filter
        const zohoInfo = _.get(filtersMap, "zohoInfo.value", null);
        if (zohoInfo && zohoInfo.selectValue && zohoInfo.inputValue) {
            const fieldPath = zohoInfo.selectValue === "contact" ? "zohoInfo.contactId" : "zohoInfo.leadId";
            const zohoValue = _.get(contact, fieldPath, "");
            if (!zohoValue.toLowerCase().includes(zohoInfo.inputValue.toLowerCase())) {
                log.info("isShowingContactList -> reason", { reason: "zohoInfo" });
                return false;
            }
        }
        //#endregion

        //#region Search Filter
        if (searchField && searchQuery) {
            log.info("isShowingContactList -> search", {
                searchField,
                searchQuery,
                contactValue: contact[searchField],
            });
            if (!(contact[searchField] || "").toLowerCase().includes(searchQuery.toLowerCase())) {
                log.info("isShowingContactList -> reason", { reason: "search" });
                return false;
            }
        }
        //#endregion

        //#region Created At Filter
        const createdAtFilter = filtersMap.createdAt;
        if (createdAtFilter) {
            log.info("isShowingContactList -> createdAt", {
                createdAtFilter,
                contactValue: contact[createdAtFilter.field],
                filterValue: new Date(createdAtFilter.value),
            });
            const { field, operator, value } = createdAtFilter;
            const contactValue = new Date(contact[field]);
            const filterValue = new Date(value);
            if (!ChatWatcherV2.checkFilterCondition(contactValue, operator, filterValue)) {
                log.info("isShowingContactList -> reason", { reason: "createdAt" });
                return false;
            }
        }
        //#endregion

        //#region Last Action Time Filter
        const lastActionTimeFilter = filtersMap.lastActionTime;
        if (lastActionTimeFilter) {
            log.info("isShowingContactList -> lastActionTime", {
                lastActionTimeFilter,
                contactValue: contact[lastActionTimeFilter.field],
                filterValue: new Date(lastActionTimeFilter.value),
            });
            const { field, operator, value } = lastActionTimeFilter;
            const contactValue = new Date(contact[field]);
            const filterValue = new Date(value);
            if (!ChatWatcherV2.checkFilterCondition(contactValue, operator, filterValue)) {
                reason = "lastActionTime";
                return false;
            }
        }
        //#endregion

        //#region Last Conversation Status Filter
        const lastConversationStatusFilter = filtersMap.lastConversationStatus;
        if (lastConversationStatusFilter) {
            log.info("isShowingContactList -> lastConversationStatus", {
                lastConversationStatusFilter,
                contactValue: contact[lastConversationStatusFilter.field],
                filterValue: lastConversationStatusFilter.value,
            });
            const { field, operator, value } = lastConversationStatusFilter;
            const contactValue = contact[field];
            const filterValue = value;
            if (!ChatWatcherV2.checkFilterCondition(contactValue, operator, filterValue)) {
                log.info("isShowingContactList -> reason", { reason: "lastConversationStatus" });
                return false;
            }
        }

        if (
            !lastConversationStatusFilter &&
            CONVERSATION_STATE_MAP[selectedState] &&
            CONVERSATION_STATE_MAP[selectedState].length > 0
        ) {
            const lastConversationStatus = contact.lastConversationStatus;
            log.info("isShowingContactList -> lastConversationStatus", { lastConversationStatus, selectedState });
            if (!CONVERSATION_STATE_MAP[selectedState].includes(lastConversationStatus)) {
                log.info("isShowingContactList -> reason", { reason: "lastConversationStatus" });
                return false;
            }
        }
        //#endregion

        //#region Vip Customer Filter
        if (filtersMap.vipCustomer) {
            log.info("isShowingContactList -> vipCustomer", {
                vipCustomerFilter: filtersMap.vipCustomer,
                contactValue: contact[filtersMap.vipCustomer.field],
                filterValue: filtersMap.vipCustomer.value,
            });
            const { field, value } = filtersMap.vipCustomer;
            const filterValue = value === "true" || value;
            if (contact[field] !== filterValue) {
                log.info("isShowingContactList -> reason", { reason: "vipCustomer" });
                return false;
            }
        }
        //#endregion

        //#region Platform Filter
        const platforms = _.get(filtersMap, "platform.value", []);
        log.info("isShowingContactList -> platforms", {
            platforms,
            contactValue: contact.platform,
        });
        if (platforms.length > 0 && !platforms.includes(contact.platform)) {
            log.info("isShowingContactList -> reason", { reason: "platform" });
            return false;
        }
        //#endregion

        //#region Tag Filter
        const tags = _.get(filtersMap, "tags.value", []);
        log.info("isShowingContactList -> tags", {
            tags,
            contactValue: contact.tags,
        });
        if (tags.length > 0) {
            const matchingTags = _.intersection(tags, contact.tags);
            if (matchingTags.length === 0) {
                log.info("isShowingContactList -> reason", { reason: "tags" });
                return false;
            }
        }
        //#endregion

        //#region Blocked Filter
        const blocked = _.get(filtersMap, "blocked.value", null);
        if (blocked) {
            log.info("isShowingContactList -> blocked", {
                blockedFilter: blocked,
                contactValue: contact.blockedSnapshot,
                filterValue: blocked,
            });
            const blockedValue = blocked === "true" || blocked;
            if (contact.blockedSnapshot !== blockedValue) {
                log.info("isShowingContactList -> reason", { reason: "blocked" });
                return false;
            }
        }
        //#endregion

        //#region Meta Referral Filter
        const metaReferrals = _.get(filtersMap, "metaReferral.value", null);
        if (metaReferrals) {
            log.info("isShowingContactList -> metaReferrals", {
                metaReferralsFilter: metaReferrals,
                contactValue: contact.metaReferrals.length,
                filterValue: metaReferrals,
            });
            const metaReferralsValue = metaReferrals === "true" || metaReferrals;
            if (!metaReferralsValue && contact.metaReferrals.length > 0) {
                log.info("isShowingContactList -> reason", { reason: "metaReferrals" });
                return false;
            }
            if (metaReferralsValue && contact.metaReferrals.length === 0) {
                log.info("isShowingContactList -> reason", { reason: "metaReferrals" });
                return false;
            }
        }
        //#endregion

        //#region Last Conversation Users Filter
        const lastConversationUsers = _.get(filtersMap, "lastConversationUsers.value", []);
        if (lastConversationUsers.length > 0) {
            log.info("isShowingContactList -> lastConversationUsers", {
                lastConversationUsersFilter: lastConversationUsers,
                contactValue: contact.lastConversationUsers,
            });
            const isNone = lastConversationUsers.find(({ type }) => type === "none");
            const agents = lastConversationUsers.filter(({ type }) => type === "agent");

            if (isNone && (contact.lastConversationUsers || []).length !== 0) {
                log.info("isShowingContactList -> reason", { reason: "lastConversationUsers" });
                return false;
            }

            if (agents.length > 0) {
                const agentIds = agents.map(({ value }) => value);
                if (!(contact.lastConversationUsers || []).some((agentId) => agentIds.includes(agentId))) {
                    log.info("isShowingContactList -> reason", { reason: "lastConversationUsers" });
                    return false;
                }
            }
        }
        //#endregion

        //#region Contact Properties Filter
        const contactProperties = Object.keys(filtersMap).filter((key) => key.startsWith("contactProperties."));
        if (contactProperties.length > 0) {
            log.info("isShowingContactList -> contactProperties", {
                contactProperties,
                contactPropertiesSnapshot: contact.contactPropertiesSnapshot,
            });
            const mapContactProperties = contactProperties.reduce((acc, key) => {
                acc[key] = filtersMap[key];
                return acc;
            }, {});

            const contactPropertiesFilter = contactProperties.reduce((acc, key) => {
                const propertyKey = key.replace("contactProperties.", "");
                if (mapContactProperties[propertyKey]) {
                    acc[propertyKey] = {
                        value: mapContactProperties[propertyKey].value,
                        operator: mapContactProperties[propertyKey].operator,
                    };
                }
                return acc;
            }, {});

            const properties = Object.keys(contactPropertiesFilter);
            let $contactPropertiesCondition = true;
            for (const propertyKey of properties) {
                const { value, operator } = contactPropertiesFilter[propertyKey];
                const propertyDetail = mapContactProperties[propertyKey];
                const path = `contactPropertiesSnapshot.${propertyKey}`;
                if (propertyDetail.type === "date") {
                    const filterValue = value ? [new Date(value[0]), new Date(value[1])] : null;
                    const contactValue = new Date(contact[path]);
                    if (!ChatWatcherV2.checkFilterCondition(contactValue, operator, filterValue)) {
                        log.info("isShowingContactList -> reason", { reason: `contactProperties.${propertyKey}` });
                        $contactPropertiesCondition = false;
                        break;
                    }
                } else {
                    if (!ChatWatcherV2.checkFilterCondition(contact[path], operator, value)) {
                        log.info("isShowingContactList -> reason", { reason: `contactProperties.${propertyKey}` });
                        $contactPropertiesCondition = false;
                        break;
                    }
                }
            }
            if (!$contactPropertiesCondition) {
                return false;
            }
        }
        //#endregion

        //#region Owner Filter
        let $ownerCondition = true;
        const ownerFilters = _.get(filtersMap, "owner.value", []);
        const showUnowned = ownerFilters.some((filter) => filter.type === "unowned");
        let showingAgentIds = ownerFilters.filter((filter) => filter.type === "agent").map((filter) => filter.value);

        // UI tarafında kullanıcı sadece kendisini seçsin veya seçmesin kontrolü UI'da yapılmalı.
        // Burada sadece seçilen filtreye uyuyor mu kontrolü yapılır.
        // if (!isUserRoot) {
        //     showingAgentIds = showingAgentIds.filter((agentId) => agentId === authUser._id);
        // }

        if (ownerFilters.length > 0) {
            log.info("isShowingContactList -> ownerFilters", {
                ownerFilters,
                showUnowned,
                isUserRoot,
                onlyAdminsShowsNoOwnerContacts,
                contactOwner: contact.owner,
            });
            if (showUnowned && !contact.owner) {
                // UI filtresi "Sahipsizleri göster" diyor, contact sahipsiz, OK.
                // Privacy zaten yukarıda halledildi, burada sadece filtreye uymayanı eliyoruz.
            } else if (showUnowned && contact.owner) {
                // Sahipsiz istendi ama sahibi var, bu filtre kriteri.
                // Ancak showingAgentIds listesinde varsa yine gösterebiliriz (OR mantığı genelde)
                // Kodun orijinalinde logic biraz karışıktı. Basitleştirelim:
                // EĞER (Sahipsiz Göster VE Sahipsizse) VEYA (Agent Listesinde Varsa) -> OK

                if (!showingAgentIds.includes(contact.owner)) {
                    // Sahipsiz değil ve istenen agentlar listesinde de yok.
                    log.info("isShowingContactList -> reason", { reason: "owner.filterMismatch" });
                    $ownerCondition = false;
                }
            } else if (!showUnowned) {
                // Sadece agent listesi seçili
                if (!showingAgentIds.includes(contact.owner)) {
                    log.info("isShowingContactList -> reason", { reason: "owner.filterMismatch" });
                    $ownerCondition = false;
                }
            }
        }
        if (!$ownerCondition) {
            return false;
        }
        //#endregion

        // Root olmayanlar için ownership kontrolü bloğu SİLİNDİ (Yukarıdaki Privacy Check'e taşındı)
        // Accessible Channels and Departments Filter bloğu SİLİNDİ (Yukarıdaki Privacy Check'e taşındı)

        //#region Channel Filter
        const channelFilters = _.get(filtersMap, "channel.value", []);
        if (channelFilters.length > 0) {
            log.info("isShowingContactList -> channelFilters", {
                channelFilters,
                contactChannel: contact.channel,
            });
            if (!channelFilters.includes(contact.channel)) {
                log.info("isShowingContactList -> reason", { reason: "channel" });
                return false;
            }
        }
        //#endregion

        //#region Department Filter
        const departmentFilters = _.get(filtersMap, "relatedDepartments.value", []);
        if (departmentFilters.length > 0) {
            // Department visibility ayarından bağımsız UI filtresi
            log.info("isShowingContactList -> departmentFilters", {
                departmentFilters,
                contactRelatedDepartments: contact.relatedDepartments,
            });
            if (!(contact.relatedDepartments || []).some((department) => departmentFilters.includes(department._id))) {
                log.info("isShowingContactList -> reason", { reason: "relatedDepartments" });
                return false;
            }
        }
        //#endregion
        return true;
    }

    async multiConversationAdd(data) {
        const conversations = data.conversations;
        for (const conversation of conversations) {
            const contact = await this.getContact(conversation.visitor._id, {
                ignoreSource: "multiConversationAdd",
            }).catch(() => {
                log.debug("multiConversationAdd -> contact not found", conversation.visitor._id);
            });
            if (contact.lastConversationStatus !== "destroyed") {
                this.joinConversation(conversation._id, {
                    reconnect: true,
                    autoAssign: true,
                });
            }
        }
    }

    // === MESSAGE FORMATTER ===
    formatSocketMessage(data) {
        // Socket'ten gelen farklı formatları DB formatına çevir
        const formatted = {
            _id: data._id || null,
            siteId: data.siteId || null,
            conversation: data.conversationId || data.conversation || null,
            description: data.message || data.description || "",
            messageType: data.messageType || data.type || "unknown",
            visitor: data.visitorId || data.visitor || null,
            user: data.userId || data.user || null,
            asset: data.asset || null,
            location: data.location || null,
            createdDate: data.createdDate || data.date || new Date().toISOString(),
            uniqMessageId: data.uniqMessageId || null,
            payload: data.payload || null,
            lastClickedButton: data.lastClickedButton || null,
            whatsappDelivered: data.whatsappDelivered !== undefined ? data.whatsappDelivered : true,
            whatsappBusinessMessageId: data.whatsappBusinessMessageId || null,
            broadcastId: data.broadcastId || null,
            gupshupMessageId: data.gupshupMessageId || null,
            lastWhatsappBusinessStatus: data.lastWhatsappBusinessStatus || null,
            lastUnipileStatus: data.lastUnipileStatus || "sent",
            metaMessageStatus: data.metaMessageStatus || null,
            reactions: data.reactions || [],
            replyTo: data.replyTo || null,
            isDeleted: data.isDeleted || false,
            isEdited: data.isEdited || false,
            editedAt: data.editedAt || null,
            metaMessageId: data.metaMessageId || null,
            whatsappAuthor: data.whatsappAuthor || null,
            externalProviderMessageId: data.externalProviderMessageId || null,
            callLog: data.callLog || null,
            jsonData: data.jsonData || null,
            translates: data.translates || [],
            baseLang: data.baseLang || "tr",
            transcribedText: data.transcribedText || null,
            seen: data.seen || false,
            isPrivate: data.isPrivate || false,
            mail: data.mail || null,
            fromBroadcast: data.fromBroadcast || false,
            fromAPI: data.fromAPI || false,
            fromAutomation: data.fromAutomation || false,
            metaResponseType: data.metaResponseType || null,
            metaPost: data.metaPost || null,
            systemMessageType: data.systemMessageType || "unknown",
        };

        // Chatbot mesajları için özel düzenleme
        if (data.messageType && data.messageType.startsWith("chatbot")) {
            if (data.payload && data.payload.text) {
                formatted.description = data.payload.text;
            }
        }

        // Bot message için özel payload yapısı
        if (data.payload && typeof data.payload === "object") {
            formatted.payload = data.payload;
            // Eğer payload'da text varsa description'a da koy
            if (data.payload.text && !formatted.description) {
                formatted.description = data.payload.text;
            }
        }

        // Typing mesajları için özel düzenleme
        if (data.type === "typing" || data.type === "rep-typing") {
            formatted.messageType = "typing";
        }

        return formatted;
    }

    // === MESSAGE EVENTS ===
    async newMessageReceived(data) {
        const contactId = data.visitor || data.visitorId;
        if (!contactId) {
            log.info("newMessageReceived -> contactId not found", data);
            return;
        }
        const invalidMessageTypes = ["customer-typing"];
        if (invalidMessageTypes.includes(data.type)) {
            log.info("newMessageReceived -> invalid message type, ignored", data.type);
            return;
        }

        // Socket mesajını DB formatına çevir
        const formattedMessage = this.formatSocketMessage(data);
        const isShowing = await ChatWatcherV2.isShowingContactList(contactId, true, null, null, "newMessageReceived");
        log.info("newMessageReceived -> isShowing", { isShowing });
        if (isShowing) {
            const contact = store.state.contact.contacts.find((c) => c._id === contactId);
            if (contact) {
                const selectedContact = store.state.contact.selectedContact;
                const isSelectedContact = selectedContact?._id === contactId;
                const isMyChat = contact.lastConversation.users.includes(store.state.auth.user._id);
                const playRequired = isMyChat && ((isSelectedContact && !this.tabVisible) || !isSelectedContact);
                if (playRequired) playSound(RESPONSE_COMMANDS.MESSAGE);
                const isSystemMessage = formattedMessage.messageType === "system";
                if (!isSystemMessage) {
                    if (!this.tabVisible) this.totalUnreadCount += 1;
                    const $set = {
                        lastMessage: formattedMessage,
                    };
                    const userMessage = formattedMessage.messageType.includes("user");
                    const visitorMessage = formattedMessage.messageType.includes("visitor");
                    if (userMessage) {
                        $set.unread = false;
                        $set.status = "sent";
                        $set.answerStatus = "answered";
                        $set.lastActionTime = _.get(data, "createdDate", new Date().toISOString());
                        $set.lastConversation = {
                            ...contact.lastConversation,
                            answerStatus: "answered",
                        };
                    } else if (visitorMessage) {
                        if (!isSelectedContact || !this.tabVisible) $set.unread = true;
                        const businessChannel = store.state.channel.channels.find(
                            (channel) => channel._id === contact.channel
                        );
                        if (businessChannel) {
                            $set.lastWhatsappBusinessActivityTS = new Date().toISOString();
                            setTimeout(() => {
                                sendSocketMessage(COMMANDS.SET_WHATSAPP_CONTACT_STATUS, {
                                    siteId: store.state.site.site._id,
                                    contactId,
                                    channelId: contact.channel,
                                    whatsappNumber: contact.whatsappNumber,
                                });
                            }, 200);
                        }
                        $set.answerStatus = "unanswered";
                        $set.lastActionTime = _.get(data, "createdDate", new Date().toISOString());
                        $set.lastConversation = {
                            ...contact.lastConversation,
                            answerStatus: "unanswered",
                        };
                    }
                    await store.dispatch("contact/updateContactFields", {
                        contactId,
                        set: $set,
                    });
                    publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                        action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                        data: { contactId, set: $set },
                    });
                }
                this.incUnitTestFor(contact.fullname, "messages");
            } else {
                const contact = await this.getContact(contactId, { ignoreSource: "newMessageReceived" }).catch(() => {
                    log.debug("newMessageReceived -> contact not found", contactId);
                });
                if (contact) {
                    this.requestStatsUpdate();
                    await store.dispatch("contact/addContact", contact);
                    publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                        action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                        data: contact,
                    });
                }
            }
            await store.dispatch("contact/appendMessage", {
                contactId,
                message: formattedMessage,
            });
        }
    }

    async messageDeleted(data) {
        let { contactId, conversationId, uniqMessageId } = data;
        if (!contactId) {
            const contact = store.getters["contact/getContactByConversationId"](conversationId);
            contactId = contact?._id;
        }
        if (contactId && uniqMessageId) {
            await store.dispatch("contact/setMessageDeleted", {
                contactId,
                uniqMessageId,
            });
        }
    }

    async messageUpdatedPartial(data) {
        let { contactId, conversationId, uniqMessageId, updatedFields } = data;
        if (!updatedFields) updatedFields = {};
        if (!contactId) {
            const contact = store.getters["contact/getContactByConversationId"](conversationId);
            contactId = contact?._id;
        }
        log.info("messageUpdatedPartial -> updatedFields", {
            contactId,
            conversationId,
            uniqMessageId,
            updatedFields,
        });
        if (contactId && uniqMessageId && Object.keys(updatedFields).length > 0) {
            await store.dispatch("contact/updateMessagePartial", {
                contactId,
                uniqMessageId,
                updatedFields,
            });
        }
    }

    async translateMessage(data) {
        let { contactId, conversationId, uniqMessageId, translates } = data;
        if (!contactId) {
            const contact = store.getters["contact/getContactByConversationId"](conversationId);
            contactId = contact?._id;
        }
        if (!translates) translates = [];
        if (contactId && uniqMessageId && translates.length > 0) {
            await store.dispatch("contact/updateMessagePartial", {
                contactId,
                uniqMessageId,
                updatedFields: {
                    translates,
                },
            });
        }
    }

    // === MESSAGE EVENTS ===

    async refreshContact(data) {
        const { contactId } = data;
        const contact = await this.getContact(contactId, {
            ignoreSource: "refreshContact",
            disableCache: true,
        }).catch(() => {
            log.debug("refreshContact -> contact not found", contactId);
        });
        if (contact) {
            const isShowing = await ChatWatcherV2.isShowingContactList(contactId, true, null, null, "refreshContact");
            const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
            if (isShowing) {
                if (alreadyExists) {
                    await store.dispatch("contact/updateContact", contact);
                    publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                        action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT,
                        data: contact,
                    });
                } else {
                    await store.dispatch("contact/addContact", contact);
                    publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                        action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                        data: contact,
                    });
                }
                this.requestStatsUpdate();
            } else if (!isShowing && alreadyExists) {
                // STICKY: Seçiliyse silme
                if (store.state.contact.selectedContact?._id === contactId) return;

                await store.dispatch("contact/removeContact", contactId);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                    data: { contactId },
                });
                this.requestStatsUpdate();
            }
        }
    }

    async newContactConnected(data) {
        const contactId = data?.visitor?._id || data?.visitorId;
        const contact = data.contactData;
        const isNewContact = data.isNewContact;
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contact || contactId,
            true,
            null,
            null,
            "newContactConnected"
        );
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        log.info("newContactConnected", {
            contactId,
            contact,
            isNewContact,
            isShowing,
            alreadyExists,
        });
        if (isNewContact) {
            const selectedFilterId = store.state.contact.selectedFilterId;
            const filters = store.state.contact.filters;
            const filterResults = await Promise.all(
                filters.map(async (filter) => {
                    const isShowing = await ChatWatcherV2.isShowingContactList(
                        contact,
                        true,
                        null,
                        null,
                        "newContactConnected",
                        filter.id
                    );
                    return isShowing && filter;
                })
            );
            const showingFilters = filterResults.filter((result) => result);
            const filtersWithoutCurrentFilter = showingFilters.filter((filter) => filter.id !== selectedFilterId);
            const filtersWithoutCurrentFilterIds = filtersWithoutCurrentFilter.map((filter) => filter.id);
            log.info("newContactConnected", {
                selectedFilterId,
                filters,
                filterResults,
                showingFilters,
                filtersWithoutCurrentFilter,
                filtersWithoutCurrentFilterIds,
            });
            store.dispatch(
                "contact/updateContactListFilters",
                filters.map((f) => {
                    if (filtersWithoutCurrentFilterIds.includes(f.id)) {
                        return {
                            ...f,
                            viewed: false,
                        };
                    }
                    return f;
                })
            );
        }

        if (isShowing) {
            const conversationId = contact.lastConversation._id;
            this.incUnitTestFor(contact.fullname, "connected");
            if (alreadyExists) {
                await store.dispatch("contact/updateContact", contact);
            } else {
                if (isNewContact) {
                    const currentCounts = store.state.contact.counts;
                    store.dispatch("contact/setCounts", {
                        all: currentCounts.all + 1,
                        open: currentCounts.open + 1,
                        closed: currentCounts.closed,
                    });
                }
                store.dispatch("contact/addContact", contact);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: contact,
                });
            }
            this.playSound(conversationId, RESPONSE_COMMANDS.NEW_CHAT_STARTED);
        } else if (!isShowing && alreadyExists) {
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;
            const currentCounts = store.state.contact.counts;
            store.dispatch("contact/setCounts", {
                all: currentCounts.all,
                open: currentCounts.open - 1,
                closed: currentCounts.closed + 1,
            });
            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId },
            });
        }
    }

    async conversationClosed(data) {
        const { contactId, contactData } = data;
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || contactId,
            true,
            null,
            null,
            "conversationClosed"
        );

        if (alreadyExists) {
            const updatedContact = {
                ...contactData,
                lastConversationStatus: "destroyed",
                lastConversation: {
                    ...contactData.lastConversation,
                    status: "destroyed",
                },
            };
            const currentCounts = store.state.contact.counts;
            store.dispatch("contact/setCounts", {
                all: currentCounts.all,
                open: currentCounts.open - 1,
                closed: currentCounts.closed + 1,
            });
            await store.dispatch("contact/updateContact", updatedContact);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT,
                data: updatedContact,
            });
        }

        if (isShowing && !alreadyExists) {
            store.dispatch("contact/addContact", contactData);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                data: contactData,
            });
        } else if (!isShowing && alreadyExists) {
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;
            this.playSound(contactData.lastConversation._id, RESPONSE_COMMANDS.CONVERSATION_CLOSED);
            const currentCounts = store.state.contact.counts;
            store.dispatch("contact/setCounts", {
                all: currentCounts.all,
                open: currentCounts.open - 1,
                closed: currentCounts.closed + 1,
            });
            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: contactId },
            });
            return;
        }
    }

    async chatAgentsChanged(data) {
        const { contactId, contactData, users } = data;
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || contactId,
            true,
            null,
            null,
            "chatAgentsChanged"
        );
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        if (isShowing) {
            if (alreadyExists) {
                const userIds = users.map((user) => (typeof user === "object" ? user._id : user));
                const updatedContact = {
                    ...alreadyExists,
                    lastConversationUsers: userIds,
                    lastConversation: {
                        ...alreadyExists.lastConversation,
                        users: userIds,
                    },
                };
                await store.dispatch("contact/updateContact", updatedContact);
                this.requestStatsUpdate();
            } else {
                await store.dispatch("contact/addContact", contactData);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: contactData,
                });
                this.requestStatsUpdate();
            }
        } else if (!isShowing && alreadyExists) {
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;

            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: contactId },
            });
            this.requestStatsUpdate();
        }
    }

    async agentJoinedChat(data) {
        let { userId, contactId, contactData } = data;
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || contactId,
            true,
            null,
            null,
            "agentJoinedChat"
        );

        if (contactData) {
            contactData.lastConversation.users = _.uniq([...(contactData.lastConversation?.users || []), userId]);
            contactData.lastConversationUsers = _.uniq([...(contactData.lastConversationUsers || []), userId]);
        }

        const isMyChat = userId === store.state.auth.user._id;
        if (isMyChat) playSound(RESPONSE_COMMANDS.CONVERSATION_ASSIGNED);
        if (alreadyExists) {
            const updatedContact = {
                ...contactData,
                lastConversationStatus: "active",
                lastConversationUsers: _.uniq([...(contactData.lastConversation?.users || []), userId]),
                lastConversation: {
                    ...contactData.lastConversation,
                    users: _.uniq([...(contactData.lastConversation?.users || []), userId]),
                    status: "active",
                },
            };
            await store.dispatch("contact/updateContact", updatedContact);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                data: { contactId, set: updatedContact },
            });
            this.requestStatsUpdate();
        }

        if (isShowing && !alreadyExists) {
            store.dispatch("contact/addContact", contactData);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                data: contactData,
            });
            this.requestStatsUpdate();
        } else if (!isShowing && alreadyExists) {
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;
            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: contactId },
            });
            this.requestStatsUpdate();
            return;
        }
    }

    async agentLeaveChat(data) {
        let { userId, contactId, contactData } = data;
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        if (contactData) {
            contactData.lastConversation.users = contactData.lastConversation.users.filter((u) => u !== userId);
            contactData.lastConversationUsers = contactData.lastConversationUsers.filter((u) => u !== userId);
        }
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || contactId,
            true,
            null,
            null,
            "agentLeaveChat"
        );

        log.info("agentLeaveChat", { alreadyExists, isShowing, contactData, contactId });

        if (alreadyExists) {
            const updatedContact = {
                ...contactData,
                lastConversationStatus: "active",
                lastConversationUsers: contactData.lastConversation.users.filter((u) => u !== userId),
                lastConversation: {
                    ...contactData.lastConversation,
                    users: contactData.lastConversation.users.filter((u) => u !== userId),
                    status: "active",
                },
            };
            await store.dispatch("contact/updateContact", updatedContact);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                data: { contactId, set: updatedContact },
            });
            this.requestStatsUpdate();
        }
        if (isShowing && !alreadyExists) {
            store.dispatch("contact/addContact", contactData);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                data: contactData,
            });
            this.requestStatsUpdate();
        } else if (!isShowing && alreadyExists) {
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;
            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: contactId },
            });
            this.requestStatsUpdate();
            return;
        }
    }

    async visitorUpdated(data) {
        const { contactId, contactData, updatingFields = {} } = data;
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || contactId,
            true,
            null,
            null,
            "visitorUpdated"
        );

        log.info("visitorUpdated", {
            contactId,
            contactData,
            updatingFields,
            alreadyExists,
            isShowing,
        });

        if (alreadyExists) {
            // Kişi mevcut ise sadece güncelleme yapılacak
            if (Object.keys(updatingFields).length > 0) {
                await store.dispatch("contact/updateContactFields", {
                    contactId,
                    set: updatingFields,
                });
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                    data: { contactId, set: updatingFields },
                });
            } else if (contactData) {
                await store.dispatch("contact/updateContact", contactData);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT,
                    data: contactData,
                });
            }
            this.requestStatsUpdate();
        }

        if (isShowing && !alreadyExists) {
            // Gözüküyor ve yoksa eklenecek
            if (contactData) {
                await store.dispatch("contact/addContact", contactData);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: contactData,
                });
                this.requestStatsUpdate();
            }
        } else if (!isShowing && alreadyExists) {
            // Gözükmüyor ve varsa listeden silinecek
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === contactId) return;

            await store.dispatch("contact/removeContact", contactId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: contactId },
            });
            this.requestStatsUpdate();
        }
    }

    whatsappContactStatus(data) {
        store
            .dispatch("contact/setWhatsappContactStatus", {
                contactId: data.contactId,
                whatsappNumber: data.whatsappNumber,
                status: data.isActive,
                channelId: data.channelId,
            })
            .then(_.noop)
            .catch(console.error);
    }

    async contactVisitPageChanged(data) {
        const { visitorId, url } = data;
        const $set = {
            visitingPage: url,
        };
        await store.dispatch("contact/updateContactFields", {
            contactId: visitorId,
            set: $set,
        });
        publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
            action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
            data: { contactId: visitorId, set: $set },
        });
    }

    async ownerChanged(data) {
        const { visitorId, ownerId, contactData } = data;
        const alreadyExists = store.state.contact.contacts.find((c) => c._id === visitorId);
        const isShowing = await ChatWatcherV2.isShowingContactList(
            contactData || visitorId,
            true,
            null,
            null,
            "ownerChanged"
        );

        log.info("ownerChanged", {
            visitorId,
            ownerId,
            contactData,
            alreadyExists,
            isShowing,
        });

        if (alreadyExists) {
            // Eğer varsa güncellesin
            if (contactData) {
                await store.dispatch("contact/updateContact", contactData);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT,
                    data: contactData,
                });
            } else {
                const $set = {
                    owner: ownerId,
                };
                await store.dispatch("contact/updateContactFields", {
                    contactId: visitorId,
                    set: $set,
                });
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                    data: { contactId: visitorId, set: $set },
                });
            }
            this.requestStatsUpdate();
        }

        if (isShowing && !alreadyExists) {
            // Eğer gözüküyor ve listede yoksa eklesin
            if (contactData) {
                await store.dispatch("contact/addContact", contactData);
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: contactData,
                });
                this.requestStatsUpdate();
            } else {
                const newContact = await store.dispatch("contact/getContactById", visitorId);
                if (newContact) {
                    await store.dispatch("contact/addContact", newContact);
                    publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                        action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                        data: newContact,
                    });
                    this.requestStatsUpdate();
                }
            }
        } else if (!isShowing && alreadyExists) {
            // Eğer gözükmüyor ve listede varsa silsin
            // STICKY: Seçiliyse silme
            if (store.state.contact.selectedContact?._id === visitorId) return;

            await store.dispatch("contact/removeContact", visitorId);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                data: { contactId: visitorId },
            });
            this.requestStatsUpdate();
        }
    }

    async noteAdded(data) {
        const { contactId } = data;
        const contact = await store.dispatch("contact/getContactById", contactId);
        if (contact) {
            const $set = {
                noteCount: contact.noteCount + 1 || 1,
            };
            await store.dispatch("contact/updateContactFields", {
                contactId: contactId,
                set: $set,
            });
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                data: { contactId, set: $set },
            });
        }
    }

    async noteDeleted(data) {
        const { contactId } = data;
        const contact = await store.dispatch("contact/getContactById", contactId);
        if (contact) {
            const $set = {
                noteCount: contact.noteCount - 1 || 0,
            };
            await store.dispatch("contact/updateContactFields", {
                contactId: contactId,
                set: $set,
            });
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                data: { contactId, set: $set },
            });
        }
    }

    async joinChatImmediately(data) {
        const { contactId } = data;
        const contact = await store.dispatch("contact/fetchContact", {
            contactId,
            ignoreSource: "joinChatImmediately",
            viewed: true,
        });
        if (contact) {
            await store.dispatch("contact/updateContact", contact);
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT,
                data: contact,
            });
            const queries = store.state.app.route.query;
            const isTabInbox = queries.tab === "inbox";
            const isViewChat = !queries.view || queries.view === "chat";
            log.debug("joinChatImmediately -> isShowing", {
                isTabInbox,
                isViewChat,
            });
            const isShowingWithFilter = await ChatWatcherV2.isShowingContactList(
                contactId,
                true,
                null,
                null,
                "joinChatImmediately"
            );
            if (isShowingWithFilter && isTabInbox && isViewChat) {
                await store.dispatch("contact/addContact", contact);
                this.requestStatsUpdate();
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: contact,
                });

                const isContactSelected = store.state.contact.selectedContact?._id === contactId;
                if (!isContactSelected) {
                    await store.dispatch("contact/setSelectedContact", contact);
                }
                return;
            }
            const isShowing = await ChatWatcherV2.isShowingContactList(
                contactId,
                false,
                null,
                null,
                "joinChatImmediately"
            );
            if (isShowing) {
                const windows = store.state.app.windows;
                const windowKeys = Object.keys(windows);
                const lastWindowKey = windowKeys[windowKeys.length - 1];
                const lastWindow = windows[lastWindowKey];

                if (
                    lastWindow &&
                    lastWindow.type === "grid-chat-window-contact" &&
                    lastWindow.payload &&
                    lastWindow.payload.contactId === contact._id
                ) {
                    return;
                }

                store.dispatch("app/addNewWindow", {
                    type: "grid-chat-window-contact",
                    title: contact.fullname || contact.email || contact.phone || "Bilinmeyen Kişi",
                    autoClose: false,
                    multiple: false,
                    fullShow: true,
                    footer: false,
                    payload: {
                        contactId: contact._id,
                    },
                });
                return;
            }
        }
    }

    async visitorBulkOperations(data) {
        const { visitorIds = [], updatingFields = {}, action } = data;

        if (!Array.isArray(visitorIds) || visitorIds.length === 0) {
            log.debug("visitorBulkOperations -> visitorIds is empty or invalid", data);
            return;
        }

        if (Object.keys(updatingFields).length === 0) {
            log.debug("visitorBulkOperations -> updatingFields is empty", data);
            return;
        }

        log.info("visitorBulkOperations -> processing", { visitorIds, updatingFields, action });

        for (const contactId of visitorIds) {
            const inContactsList = store.state.contact.contacts.some((c) => c._id === contactId);
            const inMultiChatView = store.state.contact.multiChatView.contacts.some((c) => c._id === contactId);
            const inCache = !!store.state.contact.cachedContacts[contactId];

            if (!inContactsList && !inMultiChatView && !inCache) {
                log.info("visitorBulkOperations -> contact not found anywhere", { contactId });
                continue;
            }

            const contact =
                store.state.contact.contacts.find((c) => c._id === contactId) ||
                store.state.contact.multiChatView.contacts.find((c) => c._id === contactId) ||
                store.state.contact.cachedContacts[contactId];

            if (!contact) {
                log.info("visitorBulkOperations -> contact not found in cache", { contactId });
                continue;
            }

            const processedFields = {};
            if (action === "add-tags") {
                const currentTags = Array.isArray(contact.tags) ? contact.tags : [];
                const newTags = Array.isArray(updatingFields.tags) ? updatingFields.tags : [];
                const mergedTags = _.uniq([...currentTags, ...newTags]);
                processedFields.tags = mergedTags;
            } else if (action === "add-preferred-department") {
                const currentDepts = Array.isArray(contact.relatedDepartments) ? contact.relatedDepartments : [];
                const newDepts = Array.isArray(updatingFields.relatedDepartments)
                    ? updatingFields.relatedDepartments
                    : [];
                const mergedDepts = _.uniq([...currentDepts, ...newDepts]);
                processedFields.relatedDepartments = mergedDepts;
            } else if (action === "add-preferred-agents") {
                const currentAgents = Array.isArray(contact.preferredAgents) ? contact.preferredAgents : [];
                const newAgents = Array.isArray(updatingFields.preferredAgents) ? updatingFields.preferredAgents : [];
                const mergedAgents = _.uniq([...currentAgents, ...newAgents]);
                processedFields.preferredAgents = mergedAgents;
            } else if (action === "add-contact-property") {
                const currentProps = contact.contactPropertiesSnapshot || {};
                const newProps = updatingFields.contactPropertiesSnapshot || {};
                processedFields.contactPropertiesSnapshot = Object.assign({}, currentProps, newProps);

                const currentContactProperties = Array.isArray(contact.contactProperties)
                    ? contact.contactProperties
                    : [];
                const newContactProperties = Array.isArray(updatingFields.contactPropertiesUpdated)
                    ? updatingFields.contactPropertiesUpdated
                    : [];

                const propertiesMap = {};
                currentContactProperties.forEach((prop) => {
                    if (prop.property) {
                        propertiesMap[prop.property] = prop;
                    }
                });

                newContactProperties.forEach((newProp) => {
                    if (newProp.property) {
                        propertiesMap[newProp.property] = newProp;
                    }
                });

                processedFields.contactProperties = Object.values(propertiesMap);
            } else if (action === "add-note") {
                if (updatingFields.addedNoteCount !== undefined) {
                    const currentNoteCount = contact.noteCount || 0;
                    processedFields.noteCount = currentNoteCount + updatingFields.addedNoteCount;
                }
            } else {
                Object.assign(processedFields, updatingFields);
            }

            if (processedFields.contactPropertiesUpdated !== undefined) {
                delete processedFields.contactPropertiesUpdated;
            }
            if (processedFields.addedNoteCount !== undefined) {
                delete processedFields.addedNoteCount;
            }

            await store.dispatch("contact/updateContactFields", {
                contactId,
                set: processedFields,
            });
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.UPDATE_CONTACT_FIELDS,
                data: { contactId, set: processedFields },
            });

            const isShowing = await ChatWatcherV2.isShowingContactList(
                contactId,
                true,
                null,
                null,
                "visitorBulkOperations"
            );
            const alreadyExists = store.state.contact.contacts.find((c) => c._id === contactId);
            if (isShowing) {
                const newContact = await store.dispatch("contact/getContactById", contactId);
                await store.dispatch("contact/addContact", newContact);
                this.requestStatsUpdate();
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                    data: newContact,
                });
            } else if (!isShowing && alreadyExists) {
                // STICKY: Seçiliyse silme
                if (store.state.contact.selectedContact?._id === contactId) return;

                await store.dispatch("contact/removeContact", contactId);
                this.requestStatsUpdate();
                publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                    action: TABLE_CONTACT_ACTIONS.REMOVE_CONTACT,
                    data: { contactId: contactId },
                });
            }
        }
    }

    static async startChatImmediately(data) {
        const fn = () => {
            log.debug("startChatImmediately", data);
            const site = store.state.site.site;
            const user = store.state.auth.user;

            const availableChannels = ChatWatcherV2.listAvailableChannels();
            const selectedChannel = availableChannels.find((channel) => channel._id === data.channelId);
            if (data.channelId && !selectedChannel) {
                message.error("Seçilen kanala erişim yetkiniz bulunmamaktadır.");
                return;
            }

            if (
                selectedChannel &&
                selectedChannel.type === "whatsapp-unipile" &&
                (!data.defaultInitialMessage || data.defaultInitialMessage.trim().length === 0) &&
                !data.passInitialMessage
            ) {
                publishMessage("SHOW_INITIAL_MESSAGE_MODAL", { data });
                return;
            }

            const hideMessage = message.loading(i18n.t("wp.chatStartingWait"), 0);
            const route = store.state.app.route;
            const chatStarterRoutes = ["/whatsapp-start-chat-zoho", "/whatsapp-start-chat"];
            if (chatStarterRoutes.includes(route.path)) {
                router.replace("/chat?tab=inbox&inbox=myInbox&view=chat");
            }
            sendSocketMessage(COMMANDS.USER_START_CHAT, {
                siteId: site._id,
                userId: user._id,
                ...data,
            });

            socketMessageStream
                .pipe(
                    filter((x) => x.cmd === RESPONSE_COMMANDS.JOIN_CHAT_IMMEDIATELY),
                    timeout(5000),
                    first()
                )
                .toPromise()
                .then(async (x) => {
                    message.success(i18n.t("wp.chatStarted"));
                })
                .catch((err) => {
                    message.error(i18n.t("wp.chatNotStarted"));
                })
                .finally(() => {
                    hideMessage();
                });
        };
        if (!data.channelId) {
            feature.create().beforeVisitorStartChat(fn);
        } else {
            fn();
        }
    }

    static async observeContact(contactId) {
        const hideMessage = message.loading("Sohbet'e bağlanılıyor...", 0);
        const contact = await store.dispatch("contact/fetchContact", {
            contactId,
            ignoreSource: "observeContact",
            viewed: true,
        });
        if (!contact) {
            message.error("Bu kişi bulunamadı.");
            hideMessage();
            return;
        }
        const isShowingContact = await ChatWatcherV2.isShowingContactList(
            contactId,
            false,
            null,
            null,
            "observeContact"
        );
        if (!isShowingContact) {
            message.error("Bu sohbeti görmek için yeterli yetkiniz bulunmamaktadır.");
            hideMessage();
            return;
        }
        const queries = store.state.app.route.query;
        const isTabInbox = queries.tab === "inbox";
        const isViewChat = !queries.view || queries.view === "chat";
        const isShowingWithFilter = await ChatWatcherV2.isShowingContactList(
            contactId,
            true,
            null,
            null,
            "observeContact"
        );
        if (isShowingWithFilter && isTabInbox && isViewChat) {
            await store.dispatch("contact/addContact", contact);
            this.requestStatsUpdate();
            publishMessage(SystemMessageTypes.TABLE_CONTACT_ACTION, {
                action: TABLE_CONTACT_ACTIONS.ADD_CONTACT,
                data: contact,
            });

            const isContactSelected = store.state.contact.selectedContact?._id === contactId;
            if (!isContactSelected) {
                await store.dispatch("contact/setSelectedContact", contact);
            }
            return;
        }

        const windows = store.state.app.windows;
        const windowKeys = Object.keys(windows);
        const lastWindowKey = windowKeys[windowKeys.length - 1];
        const lastWindow = windows[lastWindowKey];

        if (
            lastWindow &&
            lastWindow.type === "grid-chat-window-contact" &&
            lastWindow.payload &&
            lastWindow.payload.contactId === contact._id
        ) {
            hideMessage();
            return;
        }

        store.dispatch("app/addNewWindow", {
            type: "grid-chat-window-contact",
            title: contact.fullname || contact.email || contact.phone || "Bilinmeyen Kişi",
            autoClose: false,
            multiple: false,
            fullShow: true,
            footer: false,
            payload: { contactId: contact._id },
        });
        hideMessage();
    }

    reSync() {
        this.playedSounds = {};
        this.resetState();
        this.tabVisibleAction(true);
        this.getContacts();
        this.restartStatsPolling();
    }
}
